/**
 * Utility helper functions untuk format dan transform data dari API
 * Digunakan oleh dynamic components untuk render data
 */

/**
 * Format harga ke format Rupiah
 */
export const formatRupiah = (price) => {
  if (!price || isNaN(price)) return "0";
  return Math.floor(price).toLocaleString("id-ID");
};

/**
 * Get product image URL
 */
export const getProductImage = (product, imageBaseUrl = "") => {
  if (product.thumbnail) return product.thumbnail;
  if (product.image) return product.image;
  if (product.image_url) return product.image_url;
  if (product.images && product.images.length > 0) {
    return Array.isArray(product.images) ? product.images[0] : product.images;
  }
  return "/images/product/product-placeholder.webp";
};

/**
 * Get product price
 */
export const getProductPrice = (product) => {
  if (!product) return 0;
  // Struktur diskon baru: product.discount.unit_price_current
  const fromDiscount = product.discount?.unit_price_current;
  if (fromDiscount != null && fromDiscount !== "")
    return Number(fromDiscount) || 0;
  if (product.unit_price != null && product.unit_price !== "")
    return Number(product.unit_price) || 0;
  if (product.price != null && product.price !== "")
    return Number(product.price) || 0;
  return 0;
};

/**
 * Get product discount price
 */
export const getProductDiscountPrice = (product) => {
  if (!product) return 0;
  // Struktur diskon baru: product.discount.unit_price_discount
  const fromDiscount = product.discount?.unit_price_discount;
  if (fromDiscount != null && fromDiscount !== "")
    return Number(fromDiscount) || 0;
  if (product.discount_price != null && product.discount_price !== "")
    return Number(product.discount_price) || 0;
  if (product.sale_price != null && product.sale_price !== "")
    return Number(product.sale_price) || 0;
  if (product.promotional_price != null && product.promotional_price !== "")
    return Number(product.promotional_price) || 0;
  return getProductPrice(product);
};

/**
 * Get product rating
 */
export const getProductRating = (product) => {
  if (!product) return 0;

  const directRatingCandidates = [
    product.rating_average,
    product.average_rating,
    product.ratings_average,
    product.rating,
  ];

  for (const candidate of directRatingCandidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  if (product.reviews && product.reviews.length > 0) {
    const avgRating =
      product.reviews.reduce((sum, review) => sum + (review.rating || 0), 0) /
      product.reviews.length;
    return avgRating;
  }

  // Jika belum ada data rating, tampilkan 0 agar tidak terkesan ada rating palsu.
  return 0;
};

/**
 * Get product category name
 */
export const getProductCategory = (product) => {
  // Struktur baru: product_category.name
  if (product.product_category?.name) return product.product_category.name;
  // Backward compatibility: beberapa API lama pakai category atau category_name
  if (product.category?.name) return product.category.name;
  if (product.category_name) return product.category_name;
  if (product.categories && product.categories.length > 0) {
    return product.categories[0].name || "";
  }
  return "";
};

/**
 * URL halaman detail produk (fisik → /shop/[slug], digital → /shop/digital/[slug])
 * Untuk produk digital, slug di URL = product slug (bukan digital variant slug). Halaman digital akan
 * fetch product by slug lalu digitals by product_id.
 * @param {Object} product - Objek produk (slug/id, optional is_digital_product)
 * @returns {string}
 */
export const getProductDetailHref = (product) => {
  if (!product) return "/collection";
  const id = product.slug || product.id;
  if (!id) return "/collection";
  const isDigital =
    product.is_digital_product === true || product.is_digital === true;
  return isDigital ? `/shop/digital/${id}` : `/shop/${id}`;
};

/**
 * Cek apakah produk adalah preorder.
 * Menerima flag boolean/number/string dari API.
 */
export const isProductPreorder = (product) => {
  const value = product?.is_preorder ?? product?.isPreorder;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }
  return false;
};

/**
 * Build final image URL untuk product
 */
export const buildProductImageUrl = (productImage, imageBaseUrl = "") => {
  if (!productImage) return "/images/product/product-placeholder.webp";

  // Jika sudah full URL
  if (
    productImage.startsWith("http://") ||
    productImage.startsWith("https://")
  ) {
    return productImage;
  }

  // Jika path relatif dari public folder
  if (productImage.startsWith("/")) {
    return productImage;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const imagePath = productImage.replace(/^\//, "");
    return `${baseUrl}/${imagePath}`;
  }

  return productImage;
};

/**
 * Get banner image URL
 */
export const getBannerImageUrl = (banner, imageBaseUrl = "") => {
  if (!banner?.photo) return null;

  // Jika sudah full URL
  if (
    banner.photo.startsWith("http://") ||
    banner.photo.startsWith("https://")
  ) {
    return banner.photo;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const imagePath = banner.photo.replace(/^\//, "");
    return `${baseUrl}/${imagePath}`;
  }

  return null;
};

/**
 * Transform banners dari API ke format slides.
 * Deduplikasi by id (atau photo+link) agar config dengan banyak slot
 * + data cuma 1 tidak menghasilkan slide duplikat.
 */
export const transformBannersToSlides = (banners = [], imageBaseUrl = "") => {
  if (!banners || banners.length === 0) {
    return [];
  }

  const seen = new Set();
  const uniqueBanners = banners.filter((banner) => {
    const key =
      banner.id != null
        ? String(banner.id)
        : `${banner.photo || ""}|${banner.link || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return uniqueBanners
    .map((banner) => ({
      title: banner.name || "",
      hook: banner.short_description || "",
      desc: banner.description || "",
      image: getBannerImageUrl(banner, imageBaseUrl),
      link: banner.link || "/collection",
    }))
    .filter((banner) => Boolean(banner.image));
};

export const categoryHasProducts = (category) => {
  if (!category || typeof category !== "object") return false;

  const explicitCount = [
    category.products_count,
    category.product_count,
    category.products_total,
    category.total_products,
  ].find((value) => value !== null && value !== undefined && value !== "");

  if (explicitCount !== undefined) {
    const count = Number(explicitCount);
    return !Number.isFinite(count) || count > 0;
  }

  if (Array.isArray(category.products)) {
    return category.products.length > 0;
  }

  // Jangan menyembunyikan kategori jika API memang tidak mengirim informasi jumlah.
  return true;
};

export const filterNonEmptyCategories = (categories = []) =>
  Array.isArray(categories) ? categories.filter(categoryHasProducts) : [];

/**
 * Build category image URL
 */
export const buildCategoryImageUrl = (categoryImage, imageBaseUrl = "") => {
  if (!categoryImage) return null;

  // Jika sudah full URL
  if (
    categoryImage.startsWith("http://") ||
    categoryImage.startsWith("https://")
  ) {
    return categoryImage;
  }

  // Jika path relatif dari public folder
  if (categoryImage.startsWith("/")) {
    return categoryImage;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const imagePath = categoryImage.replace(/^\//, "");
    return `${baseUrl}/${imagePath}`;
  }

  return categoryImage;
};

/**
 * Build collection image URL
 */
export const buildCollectionImageUrl = (collection, imageBaseUrl = "") => {
  if (!collection) return "/images/collection/collection-placeholder.webp";

  const imagePath =
    collection.logo ||
    collection.image ||
    collection.photo ||
    collection.thumbnail;

  if (!imagePath) {
    // Fallback berdasarkan nama collection
    const name = collection.name?.toLowerCase() || "";
    if (name.includes("tas") || name.includes("bag")) {
      return "/images/collection/collection-placeholder.webp";
    }
    if (name.includes("sepatu") || name.includes("shoes")) {
      return "/images/collection/collection-placeholder.webp";
    }
    return "/images/collection/collection-placeholder.webp";
  }

  // Jika sudah full URL
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // Jika path relatif dari public folder
  if (imagePath.startsWith("/")) {
    return imagePath;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const path = imagePath.replace(/^\//, "");
    return `${baseUrl}/${path}`;
  }

  return imagePath;
};

/**
 * Build brand image URL
 */
export const buildBrandImageUrl = (brand, imageBaseUrl = "") => {
  if (!brand) return "/images/brand/nike.webp";

  const imagePath = brand.logo || brand.image || brand.photo || brand.thumbnail;

  if (!imagePath) {
    // Fallback ke default brand image
    return "/images/brand/nike.webp";
  }

  // Jika sudah full URL
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // Jika path relatif dari public folder
  if (imagePath.startsWith("/")) {
    return imagePath;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const path = imagePath.replace(/^\//, "");
    return `${baseUrl}/${path}`;
  }

  return imagePath;
};

/**
 * Extract color style dari Tailwind class
 * Menangani arbitrary values seperti text-[#FFFF00], bg-[#1678fa], dll.
 *
 * Tailwind arbitrary values tidak berfungsi saat runtime karena CSS tidak di-generate
 * untuk class yang dibuat secara dynamic. Solusinya adalah mengkonversi ke inline style.
 *
 * @param {string} colorClass - Tailwind color class (e.g., "text-white", "text-[#FFFF00]", "bg-[#1678fa]")
 * @param {string} type - Type of color: "text", "bg", "border"
 * @returns {{ className: string, style: object }} - Object dengan className (untuk non-arbitrary) dan style (untuk arbitrary)
 *
 * @example
 * extractColorStyle("text-white", "text")
 * // returns { className: "text-white", style: {} }
 *
 * extractColorStyle("text-[#FFFF00]", "text")
 * // returns { className: "", style: { color: "#FFFF00" } }
 *
 * extractColorStyle("bg-[#1678fa]", "bg")
 * // returns { className: "", style: { backgroundColor: "#1678fa" } }
 */
export const extractColorStyle = (colorClass, type = "text") => {
  if (!colorClass) {
    return { className: "", style: {} };
  }

  const trimmedColorClass = String(colorClass).trim();

  const cssPropertyMap = {
    text: "color",
    bg: "backgroundColor",
    border: "borderColor",
  };

  const cssProperty = cssPropertyMap[type] || cssPropertyMap.text;

  // Backward compatibility: beberapa template/data lama menyimpan warna sebagai
  // "#000000" tanpa prefix Tailwind seperti "text-[#000000]".
  if (
    /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmedColorClass) ||
    /^(rgb|hsl)a?\(.+\)$/.test(trimmedColorClass)
  ) {
    return {
      className: "",
      style: { [cssProperty]: trimmedColorClass },
    };
  }

  // Regex untuk mendeteksi arbitrary values: text-[#xxx], bg-[#xxx], border-[#xxx]
  // Support format: #RGB, #RRGGBB, #RRGGBBAA, rgb(), rgba(), hsl(), hsla()
  const arbitraryRegex = /^(text|bg|border)-\[(.+)\]$/;
  const match = trimmedColorClass.match(arbitraryRegex);

  if (match) {
    const [, prefix, colorValue] = match;

    return {
      className: "",
      style: { [cssPropertyMap[prefix] || cssProperty]: colorValue },
    };
  }

  // Jika bukan arbitrary value, kembalikan sebagai className biasa
  return {
    className: trimmedColorClass,
    style: {},
  };
};

/**
 * Helper untuk merge multiple color styles
 * @param  {...{ className: string, style: object }} colorStyles
 * @returns {{ classNames: string, styles: object }}
 */
export const mergeColorStyles = (...colorStyles) => {
  const classNames = colorStyles
    .map((cs) => cs.className)
    .filter(Boolean)
    .join(" ");

  const styles = colorStyles.reduce((acc, cs) => {
    return { ...acc, ...cs.style };
  }, {});

  return { classNames, styles };
};

/**
 * Link ke halaman event dari payload landing ads (path API "/e/slug" → route Next "/event/slug")
 */
export const getLandingEventHref = (event) => {
  if (!event || typeof event !== "object") return "/event";
  const path = event.path;
  if (typeof path === "string" && path.startsWith("/e/")) {
    const slug = path.replace(/^\/e\//, "").trim();
    if (slug) return `/event/${encodeURIComponent(slug)}`;
  }
  if (event.slug)
    return `/event/${encodeURIComponent(String(event.slug))}`;
  if (event.id != null)
    return `/event/${encodeURIComponent(String(event.id))}`;
  return "/event";
};

/**
 * Rentang tanggal event untuk locale id-ID
 */
export const formatEventDateRangeId = (startIso, endIso) => {
  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (a && b && a !== b) return `${a} – ${b}`;
  return a || b || "";
};
